import { GenericLazyString } from '@eventbrite/i18n';
import {
    Checkbox,
    ChoiceListItem,
    MinusIcon,
    PlusIcon,
    Radio,
} from '@eventbrite/marmalade';
import React, { useState } from 'react';
import { FilterItem, FilterSection } from './FilterSection';
import './NestedFilterItem.scss';
import { FilterItemProps, FilterItemTokens, FilterSectionProps } from './types';

interface NestedFilterItemTokens extends FilterItemTokens {
    '--NestedFilterItemMargin'?: string;
}

type FilterMapProps = {
    [key: string]: {
        title: GenericLazyString;
        values: { key: string; title: GenericLazyString }[];
    };
};

type NestedFilterProps = Pick<
    FilterSectionProps,
    'activeFilterOption' | 'isMobile' | 'onOptionChange'
> &
    FilterItemProps & {
        subFilterMap: FilterMapProps;
        tokens: NestedFilterItemTokens;
        initialToggleOpen?: boolean;
    };

function getSubFilters(titleToSearch: string, map: FilterMapProps) {
    const category = map[titleToSearch];
    if (category) {
        return category.values.map(({ key, title }) => ({
            display: title,
            value: key,
        }));
    }
    return {};
}

export const NestedFilterItem: React.FunctionComponent<NestedFilterProps> = ({
    ChoiceComponent = Radio,
    value,
    display,
    children,
    subFilterMap,
    activeFilterOption,
    initialToggleOpen = false,
    isMobile,
    tokens,
    onOptionChange,
}) => {
    const [toggle, setToggle] = useState(initialToggleOpen);
    const handleToggle = () => {
        setToggle(!toggle);
    };
    const radioValues = getSubFilters(value, subFilterMap);

    return (
        <>
            <div className="filterHeading">
                <div>
                    <ChoiceListItem value={value} label={display}>
                        <ChoiceComponent />
                    </ChoiceListItem>
                    {children}
                </div>
                <div className="subFilterToggle">
                    {toggle ? (
                        <MinusIcon
                            size="xsmall"
                            onClick={() => handleToggle()}
                            data-testid={`minus-icon-${value}`}
                        />
                    ) : (
                        <PlusIcon
                            size="xsmall"
                            onClick={() => handleToggle()}
                            data-testid={`plus-icon-${value}`}
                        />
                    )}
                </div>
            </div>
            {toggle && (
                <div
                    className="subFilterHeading"
                    style={tokens as React.CSSProperties}
                >
                    <FilterSection
                        filters={radioValues}
                        onOptionChange={onOptionChange}
                        activeFilterOption={activeFilterOption}
                        isMobile={isMobile}
                        tokens={tokens}
                    >
                        {(filterItem) => {
                            return (
                                <FilterItem
                                    key={filterItem.value}
                                    value={filterItem.value}
                                    display={filterItem.display}
                                    ChoiceComponent={Checkbox}
                                />
                            );
                        }}
                    </FilterSection>
                </div>
            )}
        </>
    );
};
